import { Map as ImmutableMap, fromJS, List } from "immutable";

// Actions
import {
    // Interactive Data System
    INITIALIZE_IDS,
    IDS_SET_CONTENT_FILTERS,
    IDS_RECALCULATE_CONTENT_FILTERS,
    IDS_CLEAN_CONTENT_FILTERS,
    IDS_SET_CONTENT_SORT,
    IDS_SET_INTERACTIVE_FILTER,
    IDS_REMOVE_INTERACTIVE_FILTER,
    IDS_REMOVE_INTERACTIVE_FILTERS
} from './interactive-data-system-actions';


// Initial state
const initialState = ImmutableMap(fromJS({}));


/**
 * Data card editor Reducers
 */
export default function interactiveDataSystem(state = initialState, action) {

    let newState = state;
    const contentRoute = action.contentId ?? (action?.pageId ? `${action.pageId}__${action.cardId}` : action.cardId);
    const interactiveRoute = action.pageId

    switch (action.type) {
        case INITIALIZE_IDS:
            return initialState;
        case IDS_SET_CONTENT_FILTERS:
            const prevFilters = state?.getIn(["content", contentRoute, "filters"]) || ImmutableMap({});
            return newState?.setIn(["content", contentRoute, "filters"], prevFilters?.merge(action.filters));
        case IDS_RECALCULATE_CONTENT_FILTERS:
            let newFilters = ImmutableMap({});
            action.filters?.forEach((filter) => {
                if (newState?.getIn(["content", contentRoute, "filters", filter])) {
                    newFilters = newFilters.setIn([filter], newState?.getIn(["content", contentRoute, "filters", filter]));
                }
            });
            return newState?.setIn(["content", contentRoute, "filters"], newFilters);
        case IDS_CLEAN_CONTENT_FILTERS:
            return newState?.setIn(["content", contentRoute, "filters"], ImmutableMap(fromJS({})));
        case IDS_SET_CONTENT_SORT:
            const prevSort = state?.getIn(["content", contentRoute, "sort"]) || ImmutableMap({});
            return newState?.setIn(["content", contentRoute, "sort"], prevSort?.merge(action.sort));
        case IDS_SET_INTERACTIVE_FILTER:
            const currentFilters = newState.getIn(["interactive_filters", interactiveRoute]) ?? List([]);

            // Convert current filters to map indexed by _id
            const currentMap = ImmutableMap(
                currentFilters.map(f => [f.get("_id"), f.get("values")])
            );

            // Convert incoming filters to map indexed by _id
            const incomingFiltersArray = action.filters ?? [];
            const incomingMap = ImmutableMap(
                incomingFiltersArray.map(f => [
                    f._id,
                    List.isList(f.values) ? f.values : List([f.values])
                ])
            );

            // If Ctrl is not pressed and maps are equal (only for incoming keys), toggle off only those filters
            if (!action.isCtrlPressed) {
                const isEqual = incomingFiltersArray.every(f => {
                    const currentVals = currentMap.get(f._id) ?? List([]);
                    const incomingVals = List.isList(f.values) ? f.values : List([f.values]);
                    return currentVals.equals(incomingVals);
                });

                if (isEqual) {
                    const filtered = currentFilters.filter(f => !incomingMap.has(f.get("_id")));
                    return newState.setIn(["interactive_filters", interactiveRoute], filtered);
                }
            }

            let updatedFilters = currentFilters;

            for (const incoming of incomingFiltersArray) {
                const incomingId = incoming._id;
                const incomingValues = List.isList(incoming.values)
                    ? incoming.values
                    : List([incoming.values]);

                const index = updatedFilters.findIndex(f => f.get("_id") === incomingId);
                const existingFilter = index >= 0 ? updatedFilters.get(index) : null;
                const existingValues = existingFilter
                    ? List.isList(existingFilter.get("values"))
                        ? existingFilter.get("values")
                        : List([existingFilter.get("values")])
                    : List([]);

                let newValues;

                if (action.isCtrlPressed) {
                    // Check if all incoming values are already present (combination toggle)
                    const combinationExists = incomingFiltersArray.every(f => {
                        const existing = currentMap.get(f._id) ?? List([]);
                        const incomingList = List.isList(f.values) ? f.values : List([f.values]);
                        return incomingList.every(val => existing.includes(val));
                    });

                    if (combinationExists) {
                        // Remove values from combination, but avoid emptying the filter
                        const remaining = existingValues.filter(v => !incomingValues.includes(v));
                        newValues = remaining.size > 0 ? remaining : existingValues;
                    } else {
                        // Add new values (without duplicates)
                        newValues = existingValues.concat(incomingValues.filter(v => !existingValues.includes(v)));
                    }
                } else {
                    // Replace values directly
                    newValues = incomingValues;
                }

                if (index >= 0) {
                    updatedFilters = updatedFilters.set(index, existingFilter.set("values", newValues));
                } else {
                    const newFilter = fromJS(incoming).set("values", newValues);
                    updatedFilters = updatedFilters.push(newFilter);
                }
            }

            // Keep filters that weren't in action.filters unchanged
            return newState.setIn(["interactive_filters", interactiveRoute], updatedFilters);
        case IDS_REMOVE_INTERACTIVE_FILTER:
            const prevInteractiveFilters = state?.getIn(["interactive_filters", interactiveRoute]) ?? List([])
            const filterToRemoveIndex = prevInteractiveFilters?.findIndex(interactiveFilter => interactiveFilter.get("_id") === action.filterId)
            if (filterToRemoveIndex >= 0) {
                return newState?.removeIn(["interactive_filters", interactiveRoute, filterToRemoveIndex])
            }
            return newState
        case IDS_REMOVE_INTERACTIVE_FILTERS:
            return newState?.setIn(["interactive_filters", interactiveRoute], List([]))
        default:
            return newState;
    }
};
